import dayjs, { Dayjs } from 'dayjs';
import { DaySequence, Job, JobStatus } from '../generated/gql-types';
import {
  getJobDisplayScheduleEndDay,
  getJobDisplayScheduleStartDay,
  getShiftScheduleInstantsForAnchorDate,
} from './jobScheduleBounds.util';

const CLIENT_DEFAULT_GRACE_HOURS = 1;

export type ManualTimesheetEntryPortal = 'tenant' | 'client';

export type ManualTimesheetEntryOptions = {
  /** When true (default), job must be {@link JobStatus.Filled}. */
  requireFilled?: boolean;
  /**
   * Tenant: manual entry once the shift has started (no shift-end grace gate).
   * Client: shift must have ended plus {@link graceHoursAfterShiftEnd} (default 1h).
   */
  portal?: ManualTimesheetEntryPortal;
  /** Client portal only. Hours after shift end before manual entry. Default 1. */
  graceHoursAfterShiftEnd?: number;
  /** Wall-clock instant for eligibility (defaults to now). */
  asOf?: Dayjs;
};

/** Whether any day sequence covers this schedule anchor day. */
export const anchorDayHasTimesheet = (
  daySequences: Pick<DaySequence, 'startDate' | 'startDateISO'>[],
  anchorDay: Dayjs,
): boolean => {
  const anchorISO = anchorDay.format('YYYY-MM-DD');
  return daySequences.some((ds) => {
    if (ds.startDateISO === anchorISO) {
      return true;
    }
    const ms = Number(ds.startDate);
    return !Number.isNaN(ms) && dayjs(ms).isSame(anchorDay, 'day');
  });
};

/**
 * Whether this anchor day qualifies for manual backfill.
 * Tenant: shift started, no row yet. Client: same plus shift ended + grace.
 * Overnight-aware via {@link getShiftScheduleInstantsForAnchorDate}.
 */
export const isAnchorDayEligibleForManualEntry = (
  job: Job,
  anchorDay: Dayjs,
  daySequences: Pick<DaySequence, 'startDate' | 'startDateISO'>[],
  now: Dayjs = dayjs(),
  portal: ManualTimesheetEntryPortal = 'client',
  graceHours: number = CLIENT_DEFAULT_GRACE_HOURS,
): boolean => {
  if (anchorDayHasTimesheet(daySequences, anchorDay)) {
    return false;
  }

  const bounds = getShiftScheduleInstantsForAnchorDate(
    job,
    anchorDay.format('YYYY-MM-DD'),
  );
  if (!bounds) {
    return false;
  }

  if (now.isBefore(bounds.start)) {
    return false;
  }

  if (portal === 'client') {
    const canEnterAfter = bounds.end.add(graceHours, 'hour');
    if (!now.isAfter(canEnterAfter)) {
      return false;
    }
  }

  return true;
};

/**
 * Show manual timesheet create when at least one schedule day is eligible.
 */
export const canAllowManualTimesheetEntry = (
  job: Job | null | undefined,
  daySequences: Pick<DaySequence, 'startDate' | 'startDateISO'>[],
  options?: ManualTimesheetEntryOptions,
): boolean => {
  if (!job?.startDate || !job?.userId) {
    return false;
  }

  if (
    options?.requireFilled !== false &&
    job.itemStatus !== JobStatus.Filled
  ) {
    return false;
  }

  const now = options?.asOf ?? dayjs();
  const scheduleStart = getJobDisplayScheduleStartDay(job);
  const scheduleEnd = getJobDisplayScheduleEndDay(job);
  if (!scheduleStart || !scheduleEnd) {
    return false;
  }

  if (now.isBefore(scheduleStart, 'day')) {
    return false;
  }

  const portal = options?.portal ?? 'client';
  const grace =
    options?.graceHoursAfterShiftEnd ?? CLIENT_DEFAULT_GRACE_HOURS;
  let cursor = scheduleStart;

  while (
    cursor.isBefore(scheduleEnd, 'day') ||
    cursor.isSame(scheduleEnd, 'day')
  ) {
    if (
      isAnchorDayEligibleForManualEntry(
        job,
        cursor,
        daySequences,
        now,
        portal,
        grace,
      )
    ) {
      return true;
    }
    cursor = cursor.add(1, 'day');
  }

  return false;
};
